import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useLocation } from "react-router-dom";

import { capitalize } from "lodash";
import { cardProcessorService } from "../services/cardProcessorService";
import { decrypt, encrypt, encodeBase64 } from "../utils/crypto";
import api from "../services/api";
import { notifyError } from "../utils/notifications";
import { storageService } from "../services/storageService";
import { getClientDisplayName } from "../utils/socialName";

// To do: tipar esse contexto e alterar nos arquivos tsx que vão reclamar da mudança de tipagem

export type ClientTitular = {
  id: number;
  name: string;
  cpf?: string;
  socialName?: string;
  allowsSocialName?: boolean;
  dataCadastro?: string | null;
} & Record<any, any>;

const normalizeIsProdutoBandeirado = (value: unknown) =>
  value === true || value === "S";

const ClientContext = createContext<{
  client?: any;
  updateClient?: (newClient: any) => void;
  clearClient?: () => void;
  refreshClient?: () => Promise<void>;
  refreshClientV2?: (
    usePreviousClientData?: boolean,
    productIdOverride?: number
  ) => Promise<void>;
  isLoadingClient?: boolean;
}>({});

interface ClientProviderInterface {
  children: ReactNode;
}

export function ClientProvider({ children }: ClientProviderInterface) {

  const [client, setClient] = useState(() => storageService.getClient());
  const [isLoadingClient, setIsLoadingClient] = useState(false);

  useEffect(() => {
    storageService.clearLegacySocialNameFlag();
  }, []);

  useEffect(() => {
    const displayName = getClientDisplayName(client?.client);
    if (displayName) {
      document.title = `${displayName} - RPE - Atendimento`;
    } else {
      document.title = "RPE - Atendimento";
    }

    return () => { document.title = "RPE - Atendimento"; };
  }, [client]);

  const updateClient = useCallback((newClient) => {
    storageService.setClient(newClient);
    setClient(newClient);
  }, []);

  const clearClient = useCallback(() => {
    storageService.clearAttendance();
    setClient(undefined);
  }, []);

  const refreshClient = useCallback(async () => {
    const {
      transicaoBandeira,
      produtoTransicaoBandeira,
      produto: { id: codigoProduto, isProdutoBandeirado, descricao, isProdutoDigital },
    } = await cardProcessorService.getClientId(client?.client.id);

    const updatedClient = {
      ...client,
      client: {
        ...client.client,
        transicaoBandeira,
        isProdutoBandeirado: normalizeIsProdutoBandeirado(isProdutoBandeirado),
        produtoTransicaoBandeira,
      },
      product: {
        ...client.product,
        codigoProduto,
        desc_prod: descricao,
        isProdutoDigital,
      },
    };

    updateClient(updatedClient);
  }, [client, updateClient]);

  const refreshClientV2 = useCallback(
    async (usePreviousClientData = true, productIdOverride = undefined) => {

      if (!client?.client?.id) {
        return;
      }

      try {
        setIsLoadingClient(true);

        let productId = productIdOverride ?? client?.product?.codigoProduto;
        let productUpdate = client?.product;
        let clientHorizonFields: Record<string, unknown> = {};

        try {
          const {
            transicaoBandeira,
            produtoTransicaoBandeira,
            produto: {
              id: codigoProduto,
              isProdutoBandeirado,
              descricao,
              isProdutoDigital,
            },
          } = await cardProcessorService.getClientId(client?.client.id);

          productId = productIdOverride ?? codigoProduto;
          productUpdate = {
            ...client?.product,
            codigoProduto: productId,
            desc_prod: descricao,
            isProdutoDigital,
          };
          clientHorizonFields = {
            transicaoBandeira,
            isProdutoBandeirado:
              normalizeIsProdutoBandeirado(isProdutoBandeirado),
            produtoTransicaoBandeira,
          };
        } catch (productSyncError) {
          if (productIdOverride != null) {
            productId = productIdOverride;
            productUpdate = {
              ...client?.product,
              codigoProduto: productIdOverride,
            };
          }
          console.error(
            "Erro ao sincronizar produto do portador na Horizon:",
            productSyncError
          );
        }

        const response = await api.get("client_id", {
          client_id: client?.client.id,
          product_id: productId,
          shouldLoadTicket: true,
          ticketId: client?.client?.ticket?.id,
        });
        const clientFound = response.data;

        // Essas transformações são feitas no método handleAttend do arquivo src/components/SearchUser/index.jsx
        if (clientFound.endereco_alternativo) {
          clientFound.endereco_alternativo.bairro = capitalize(
            clientFound.endereco_alternativo.bairro
              ? clientFound.endereco_alternativo.bairro
              : ""
          );
          clientFound.endereco_alternativo.localidade = capitalize(
            clientFound.endereco_alternativo.localidade
              ? clientFound.endereco_alternativo.localidade
              : ""
          );
          clientFound.endereco_alternativo.cidade = capitalize(
            clientFound.endereco_alternativo.cidade
              ? clientFound.endereco_alternativo.cidade
              : ""
          );
          clientFound.endereco_alternativo.logradouro = capitalize(
            clientFound.endereco_alternativo.logradouro
              ? clientFound.endereco_alternativo.logradouro
              : ""
          );
          clientFound.endereco_alternativo.complemento = capitalize(
            clientFound.endereco_alternativo.complemento
              ? clientFound.endereco_alternativo.complemento
              : ""
          );
        }

        if (clientFound.endereco_comercial) {
          clientFound.endereco_comercial.empresa = capitalize(
            clientFound.endereco_comercial.empresa
              ? clientFound.endereco_comercial.empresa
              : ""
          );
          clientFound.endereco_comercial.departamento = capitalize(
            clientFound.endereco_comercial.departamento
              ? clientFound.endereco_comercial.departamento
              : ""
          );
          clientFound.endereco_comercial.bairro = capitalize(
            clientFound.endereco_comercial.bairro
              ? clientFound.endereco_comercial.bairro
              : ""
          );
          clientFound.endereco_comercial.localidade = capitalize(
            clientFound.endereco_comercial.localidade
              ? clientFound.endereco_comercial.localidade
              : ""
          );
          clientFound.endereco_comercial.cidade = capitalize(
            clientFound.endereco_comercial.cidade
              ? clientFound.endereco_comercial.cidade
              : ""
          );
          clientFound.endereco_comercial.logradouro = capitalize(
            clientFound.endereco_comercial.logradouro
              ? clientFound.endereco_comercial.logradouro
              : ""
          );
          clientFound.endereco_comercial.complemento = capitalize(
            clientFound.endereco_comercial.complemento
              ? clientFound.endereco_comercial.complemento
              : ""
          );
        }

        if (clientFound.endereco_residencial) {
          clientFound.endereco_residencial.bairro = capitalize(
            clientFound.endereco_residencial.bairro
              ? clientFound.endereco_residencial.bairro
              : ""
          );
          clientFound.endereco_residencial.localidade = capitalize(
            clientFound.endereco_residencial.localidade
              ? clientFound.endereco_residencial.localidade
              : ""
          );
          clientFound.endereco_residencial.cidade = capitalize(
            clientFound.endereco_residencial.cidade
              ? clientFound.endereco_residencial.cidade
              : ""
          );
          clientFound.endereco_residencial.logradouro = capitalize(
            clientFound.endereco_residencial.logradouro
              ? clientFound.endereco_residencial.logradouro
              : ""
          );
          clientFound.endereco_residencial.complemento = capitalize(
            clientFound.endereco_residencial.complemento
              ? clientFound.endereco_residencial.complemento
              : ""
          );
        }

        const decryptedBirthday = clientFound.birthday
          ? decrypt(clientFound.birthday)
          : undefined;

        const insideClient = usePreviousClientData
          ? {
            ...client.client,
            ...clientFound,
            ...clientHorizonFields,
            birthday: decryptedBirthday,
          }
          : {
            ...clientFound,
            ticket: clientFound.ticket || client.client.ticket,
            ...clientHorizonFields,
            birthday: decryptedBirthday,
          };

        const updatedClient = {
          ...client,
          client: insideClient,
          product: productUpdate,
        };

        updateClient(updatedClient);
      } catch (err) {
        const error = err as ErrorMessage;
        console.error("Erro ao atualizar dados do portador: ", error.message);
        notifyError("Houve um erro ao atualizar os dados do portador");
      } finally {
        setIsLoadingClient(false);
      }
    },
    [client, updateClient]
  );

  const value = useMemo(
    () => ({
      client,
      updateClient,
      clearClient,
      refreshClient,
      refreshClientV2,
      isLoadingClient,
    }),
    [
      client,
      updateClient,
      clearClient,
      refreshClient,
      refreshClientV2,
      isLoadingClient,
    ]
  );

  useEffect(() => {
    if (client?.client?.id) {
      refreshClientV2(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.client?.id]);

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
}

export function useClient() {
  const context = useContext(ClientContext);

  if (!context) {
    throw new Error("useClient must be used within an ClientProvider");
  }

  return context;
}
const App = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  return location.pathname !== "/" ? (
    <ClientProvider>{children}</ClientProvider>
  ) : (
    <>{children}</>
  );
};

export default App;
